import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useAppStore } from "@/stores";
import {
  RevenueIntelligenceChatCatalogueEntry,
  RevenueIntelligenceChatRegistrationValue,
  useRevenueIntelligenceChat,
} from "./RevenueIntelligenceChatContext";
import { resolvePersistentRevenueIntelligenceSignals } from "../lib/revenue-intelligence-signals";

type ResolvableCatalogueId =
  | "state_of_sales.forecast"
  | "state_of_sales.pipeline_risk"
  | "state_of_sales.sales_economics";

type PendingResolution = {
  reject: (reason?: unknown) => void;
  resolve: (contracts: RevenueIntelligenceChatRegistrationValue[]) => void;
  timeoutId: number;
};

const StateOfSalesResolver = lazy(() => import(
  "../state-of-sales-v2/RevenueIntelligenceStateOfSalesChatResolver"
));
const SalesEconomicsResolver = lazy(() => import(
  "../state-of-sales-v2/RevenueIntelligenceSalesEconomicsChatResolver"
));

export function RevenueIntelligenceGlobalChatCatalogue() {
  const currentUser = useAppStore(state => state.currentUser);
  const {
    registerCatalogueEntry,
    unregisterCatalogueEntry,
  } = useRevenueIntelligenceChat();
  const [requested, setRequested] = useState<Set<ResolvableCatalogueId>>(
    () => new Set(),
  );
  const pendingRef = useRef(new Map<ResolvableCatalogueId, PendingResolution[]>());
  const resolvedRef = useRef(new Map<ResolvableCatalogueId, RevenueIntelligenceChatRegistrationValue[]>());
  const actorKey = `${currentUser?.company_id ?? "none"}:${currentUser?.id ?? "none"}:${currentUser?.role ?? "none"}`;

  const requestContracts = useCallback((catalogueId: ResolvableCatalogueId) => {
    const cached = resolvedRef.current.get(catalogueId);
    if (cached?.length) return Promise.resolve(cached);
    setRequested(current => new Set(current).add(catalogueId));
    return new Promise<RevenueIntelligenceChatRegistrationValue[]>((resolve, reject) => {
      const pending = pendingRef.current.get(catalogueId) ?? [];
      const item: PendingResolution = {
        reject,
        resolve,
        timeoutId: window.setTimeout(() => {
          const active = pendingRef.current.get(catalogueId) ?? [];
          pendingRef.current.set(catalogueId, active.filter(candidate => candidate !== item));
          reject(new Error(`${catalogueId} did not resolve within the loading budget.`));
        }, 45_000),
      };
      pending.push(item);
      pendingRef.current.set(catalogueId, pending);
    });
  }, []);

  const settleResolution = useCallback((
    catalogueId: ResolvableCatalogueId,
    contracts: RevenueIntelligenceChatRegistrationValue[],
  ) => {
    if (!contracts.length) return;
    resolvedRef.current.set(catalogueId, contracts);
    const pending = pendingRef.current.get(catalogueId) ?? [];
    pending.forEach(item => {
      window.clearTimeout(item.timeoutId);
      item.resolve(contracts);
    });
    pendingRef.current.delete(catalogueId);
  }, []);

  const rejectResolution = useCallback((
    catalogueId: ResolvableCatalogueId,
    error: unknown,
  ) => {
    const pending = pendingRef.current.get(catalogueId) ?? [];
    pending.forEach(item => {
      window.clearTimeout(item.timeoutId);
      item.reject(error);
    });
    pendingRef.current.delete(catalogueId);
  }, []);

  useEffect(() => {
    resolvedRef.current.clear();
    setRequested(new Set());
    for (const pending of pendingRef.current.values()) {
      pending.forEach(item => {
        window.clearTimeout(item.timeoutId);
        item.reject(new Error("Revenue Intelligence access changed."));
      });
    }
    pendingRef.current.clear();
  }, [actorKey]);

  useEffect(() => {
    if (!currentUser?.company_id) return;
    const accessPolicy = {
      allowedRoles: ["super_admin", "admin"] as Array<"super_admin" | "admin">,
      companyId: String(currentUser.company_id),
      dataScope: "company" as const,
      enforcement: "company_role_gate" as const,
    };
    const entries: RevenueIntelligenceChatCatalogueEntry[] = [
      {
        catalogueId: "revenue_intelligence.persistent_signals",
        contractIds: ["revenue_intelligence.persistent_signals"],
        label: "Signals",
        topics: [
          "signals",
          "warnings",
          "alerts",
          "email signals",
          "email evidence",
          "email excerpt",
          "buyer email",
          "buyer communication",
          "buying signal",
          "risk signal",
          "buying signals",
          "risk signals",
          "budget risk",
          "budget",
          "delay",
          "timeline",
          "mejl",
          "mejlutdrag",
          "köparmejl",
          "köparkommunikation",
          "köpsignal",
          "köpsignaler",
          "köpsignalen",
          "risksignal",
          "risksignaler",
          "risksignalen",
          "budgetrisk",
          "varningar",
          "nya varningar",
        ],
        suggestedQuestions: ["Show the most important Signals"],
        accessPolicy,
        resolve: () => resolvePersistentRevenueIntelligenceSignals({
          companyId: String(currentUser.company_id),
        }),
      },
      {
        catalogueId: "state_of_sales.forecast",
        contractIds: ["state_of_sales.forecast", "state_of_sales.forecast.overview"],
        label: "Forecast",
        topics: ["forecast position", "state of sales", "forecast", "prognos"],
        suggestedQuestions: ["Where is the forecast exposed?"],
        accessPolicy,
        resolve: () => requestContracts("state_of_sales.forecast"),
      },
      {
        catalogueId: "state_of_sales.pipeline_risk",
        contractIds: ["state_of_sales.pipeline_risk"],
        label: "Pipeline Risk",
        topics: ["pipeline risk", "risk exposure", "deal risk", "riskiest", "risk"],
        suggestedQuestions: ["Which deals have the greatest Pipeline Risk?"],
        accessPolicy,
        resolve: () => requestContracts("state_of_sales.pipeline_risk"),
      },
      {
        catalogueId: "state_of_sales.sales_economics",
        contractIds: ["state_of_sales.growth_efficiency_v2"],
        label: "Sales Economics",
        topics: [
          "revenue per sales hour",
          "growth efficiency",
          "sales economics",
          "sales time",
          "säljekonomi",
          "tid på vunna eller förlorade affärer",
          "tid på vunna affärer",
          "tid på förlorade affärer",
        ],
        suggestedQuestions: ["What is changing in Sales Economics?"],
        accessPolicy,
        resolve: () => requestContracts("state_of_sales.sales_economics"),
      },
      {
        catalogueId: "management_review",
        contractIds: [
          "management_review.prepared_deal_facts",
          "management_review.executive_insights",
        ],
        label: "Management Review",
        topics: ["management review"],
        suggestedQuestions: [],
        accessPolicy,
        unavailableReason: "Management Review is available in its own view, but its reviewed analysis output is not yet independently resolvable.",
      },
      {
        catalogueId: "state_of_sales.pipeline_health",
        contractIds: [],
        label: "Pipeline Health",
        topics: ["pipeline coverage", "pipeline health"],
        suggestedQuestions: [],
        accessPolicy,
        unavailableReason: "Pipeline Health does not have an approved chat projection yet.",
      },
    ];
    entries.forEach(registerCatalogueEntry);
    return () => entries.forEach(entry => unregisterCatalogueEntry(entry.catalogueId));
  }, [currentUser?.company_id, registerCatalogueEntry, requestContracts, unregisterCatalogueEntry]);

  const requestedStateOfSales = [...requested].filter(id => (
    id === "state_of_sales.forecast" || id === "state_of_sales.pipeline_risk"
  ));

  return (
    <Suspense fallback={null}>
      {requestedStateOfSales.length > 0 && (
        <StateOfSalesResolver
          requested={requestedStateOfSales}
          onError={rejectResolution}
          onResolved={settleResolution}
        />
      )}
      {requested.has("state_of_sales.sales_economics") && (
        <SalesEconomicsResolver
          onError={error => rejectResolution("state_of_sales.sales_economics", error)}
          onResolved={contracts => settleResolution("state_of_sales.sales_economics", contracts)}
        />
      )}
    </Suspense>
  );
}
